import { css } from '@emotion/react'
import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { colors, CompanyLogo, font, mq } from 'plume-ui'
import { formatDate } from '@/lib/date'
import { investorUrl } from '@/lib/url'
import { CompanySummary, InvestorSummary } from '../../../types'
import { ShareMenu } from '../../atoms/ShareMenu'
import { checkSummaryStickyPosition } from '@/lib/sticky'
import { CompanyAndInvestorSummaryBg } from '@/components/common/CompanyAndInvestorSummaryBg'

type Props = {
  summary: InvestorSummary
}

const COVER_IMAGE_COUNT = 7

export const InvestorSummarySection: React.FC<Props> = (props) => {
  const url = investorUrl(props.summary.shortname)
  const cover = randomImageById(COVER_IMAGE_COUNT, props.summary.id)

  const [isInvestorSummaryStickyPosition, setIsInvestorSummaryStickyPosition] =
    useState(false)

  useEffect(() => {
    window.addEventListener('scroll', () => {
      setIsInvestorSummaryStickyPosition(checkSummaryStickyPosition)
    })
    return () => {
      window.removeEventListener('scroll', () => {
        setIsInvestorSummaryStickyPosition(checkSummaryStickyPosition)
      })
    }
  }, [])

  return (
    <div
      className="summary"
      css={css`
        display: inline;
      `}
    >
      <CompanyAndInvestorSummaryBg
        backgroundImage={cover}
        sticky={isInvestorSummaryStickyPosition}
        css={css`
          ${mq.large} {
            margin-block-start: 24px;
          }
        `}
      >
        <div
          css={css`
            position: absolute;
            z-index: 1;
            display: flex;
            justify-content: end;
            width: 100%;
            padding-block-start: 12px;
            padding-inline: 24px;
            ${mq.large} {
              position: static;
              padding-inline: 16px 38px;
            }
          `}
        >
          {props.summary.updatedAt && (
            <p
              css={css`
                margin: 0;
                color: ${colors.white00};
                font-size: 12px;
                line-height: 1.25;
                letter-spacing: 0.02em;
              `}
              className="last-updated-at"
            >
              <span>最終更新日</span>
              <span
                css={css`
                  padding: 0 5px;
                `}
              >
                :
              </span>
              <span suppressHydrationWarning>
                {formatDate(props.summary.updatedAt, 'yyyy/MM/dd HH:mm')}
              </span>
            </p>
          )}
        </div>
        <div
          css={css`
            display: flex;
            align-items: flex-end;
            width: 100%;
            max-width: ${isInvestorSummaryStickyPosition && '1120px'};
            margin-block-start: auto;
            margin-inline: auto;
            padding-inline: 24px;
            color: ${colors.textInvert};
            ${mq.large} {
              padding-inline: ${isInvestorSummaryStickyPosition ? '0' : '80px'};
            }
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: ${isInvestorSummaryStickyPosition
                ? 'center'
                : 'end'};
              justify-content: space-between;
              column-gap: 24px;
              width: 100%;
              ${mq.large} {
                min-height: ${isInvestorSummaryStickyPosition
                  ? '96px'
                  : '80px'};
              }
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: start;
                flex-direction: column;
                column-gap: ${isInvestorSummaryStickyPosition
                  ? '16px'
                  : '32px'};
                row-gap: 16px;
                min-width: 0;
                margin-block-start: 148px;
                ${mq.large} {
                  align-items: ${isInvestorSummaryStickyPosition
                    ? 'center'
                    : 'end'};
                  flex-direction: row;
                  margin: 0;
                }
              `}
            >
              <div
                css={css`
                  flex-grow: 0;
                  flex-shrink: 0;
                  ${mq.large} {
                    margin-block-end: ${isInvestorSummaryStickyPosition
                      ? '0'
                      : '-80px'};
                  }
                `}
              >
                <CompanyLogo
                  src={props.summary.imageUrl}
                  name={props.summary.name}
                  size={
                    isInvestorSummaryStickyPosition ? 'normal' : 'extra-large'
                  }
                  css={css`
                    ${mq.small} {
                      width: 96px !important;
                      height: 96px !important;
                    }
                  `}
                />
              </div>
              <div
                className="desktop"
                css={css`
                  display: flex;
                  flex-direction: column;
                  row-gap: 4px;
                  width: 100%;
                  min-width: 0;
                  ${mq.large} {
                    row-gap: 2px;
                    margin-block-end: ${isInvestorSummaryStickyPosition
                      ? '0'
                      : '8px'};
                  }
                `}
              >
                <CorporateNumber>
                  {props.summary.corporateNumber}
                </CorporateNumber>
                <h1
                  css={css`
                    margin: 0;
                    color: ${colors.text};
                    font-size: 24px;
                    font-weight: ${font.weight.bold};
                    line-height: 1.5;
                    letter-spacing: 0.01em;
                    overflow-wrap: break-word;
                    transition: font-size 0.16s 0s linear;
                    ${mq.large} {
                      color: ${colors.textInvert};
                      font-size: ${isInvestorSummaryStickyPosition
                        ? '28px'
                        : '32px'};
                      line-height: 1.25;
                      ${isInvestorSummaryStickyPosition &&
                      'text-overflow: ellipsis; overflow: hidden; white-space: nowrap;'}
                    }
                  `}
                >
                  {props.summary.name}
                </h1>
              </div>
            </div>
            <div
              css={css`
                display: none;
                ${mq.large} {
                  flex-grow: 0;
                  flex-shrink: 0;
                  display: flex;
                  align-items: center;
                  margin-block-end: ${isInvestorSummaryStickyPosition
                    ? '0'
                    : '16px'};
                }
              `}
            >
              <ShareMenu theme="dark-bg" url={url} title={props.summary.name} />
              {/* フォロー通知を再開するときにコメントアウトを外す */}
              {/* {isEnterpriseWithTrial(user) && (
                  <>
                    <VerticalDivider
                      height="25px"
                      color={colors.white00}
                      css={css`
                        margin-left: 15px;
                        margin-right: 20px;
                      `}
                    />
                    <InvestorFollowButton summary={props.summary} />
                  </>
                )} */}
            </div>
          </div>
        </div>
      </CompanyAndInvestorSummaryBg>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          max-width: 1200px;
          margin: 0 auto;
          min-height: 100px;
        `}
      >
        <div
          css={css`
            margin-top: 16px;
            padding: 0px 20px 0px 32px;
            ${mq.large} {
              margin: 10px 0 10px 185px;
            }
          `}
        >
          <Description>{props.summary.description}</Description>
          <MajorDestination
            shortname={props.summary.shortname}
            list={props.summary.companies}
          />
        </div>
      </div>
    </div>
  )
}

const randomImageById = (imgCount: number, id: number) => {
  const n = (id % imgCount) + 1
  return `/static/images/investor-cover/investor-cover_00${n}.jpg`
}

const CorporateNumber: React.FC<{
  children?: string
  className?: string
}> = (props) => {
  return props.children ? (
    <p
      css={css`
        margin: 0;
        color: ${colors.textLight};
        font-size: 13px;
        letter-spacing: 0.5px;
        ${mq.large} {
          color: ${colors.textInvert};
        }
      `}
      className={props.className}
    >
      法人番号
      <span
        css={css`
          padding: 0 5px;
        `}
      >
        :
      </span>
      <span>{props.children}</span>
    </p>
  ) : null
}

type DescriptionProps = {
  children: React.ReactNode
}

const Description: React.FC<DescriptionProps> = (props) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isOmmited, setIsOmmited] = useState<boolean | null>(null)

  useEffect(() => {
    if (!ref || !ref.current) {
      return
    }
    if (isOverflown(ref.current)) {
      setIsOmmited(true)
    } else {
      setIsOmmited(false)
    }
  }, [])

  return (
    <div
      css={css`
        position: relative;
        padding-bottom: 12px;
      `}
      className="description"
    >
      <div
        ref={ref}
        css={css`
          font-size: 13px;
          line-height: 1.6;
          color: ${colors.black30};
          white-space: pre-wrap;
          ${isOmmited !== false && omitStyle};
          ${isOmmited && 'margin-bottom: 4px'}
        `}
      >
        {props.children}
      </div>
      {isOmmited && (
        <a
          onClick={() => setIsOmmited(false)}
          css={css`
            color: ${colors.link};
            text-decoration: underline;
            font-size: 13px;
            position: absolute;
            right: 0;
            bottom: 0;
          `}
        >
          すべて見る
        </a>
      )}
    </div>
  )
}

const omitStyle = css`
  max-height: calc(13px * 1.6 * 3);
  -webkit-line-clamp: 3;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const isOverflown = (elm: HTMLDivElement) => {
  return (
    elm.scrollHeight > elm.clientHeight || elm.scrollWidth > elm.clientWidth
  )
}

const isDefaultLogo = (img: string) =>
  img.startsWith('/assets/default_company') || img.length === 0

const MAX_DISPLAY_COUNT = 9

const MajorDestination: React.FC<{
  shortname: string
  list: CompanySummary[]
}> = (props) => {
  const isOverDisplayCount = props.list.length > MAX_DISPLAY_COUNT
  const displayableList = isOverDisplayCount
    ? props.list.slice(0, 8)
    : props.list.slice(0, 9)

  return (
    <div
      css={css`
        padding: 10px 0;
      `}
    >
      {props.list.length > 0 && (
        <>
          <span
            className="investment-destination"
            css={css`
              font-size: 14px;
              color: ${colors.textLight};
            `}
          >
            投資先
          </span>
          <div
            className="investments"
            css={css`
              display: flex;
              padding: 10px 0;
              flex-wrap: wrap;
            `}
          >
            {displayableList.map((c) => (
              <Link href={c.path} as={c.path} key={c.path} passHref>
                <a className="company-logo-item">
                  {isDefaultLogo(c.imageUrl) ? (
                    <div
                      css={css`
                        margin-right: 10px;
                        margin-bottom: 10px;
                      `}
                    >
                      <HoverEffect>
                        <CompanyLogo.Frame
                          size="large"
                          css={css`
                            font-size: 11px;
                            text-align: center;
                            color: ${colors.gray90};
                            line-height: 1.2;
                          `}
                        >
                          <span>{c.name.replace('株式会社', '')}</span>
                        </CompanyLogo.Frame>
                      </HoverEffect>
                    </div>
                  ) : (
                    <div
                      css={css`
                        margin-right: 10px;
                        margin-bottom: 10px;
                      `}
                    >
                      <HoverEffect>
                        <CompanyLogo
                          src={c.imageUrl}
                          name={c.name}
                          size={'large'}
                        />
                      </HoverEffect>
                    </div>
                  )}
                </a>
              </Link>
            ))}
            {isOverDisplayCount && (
              <Link
                href={`/investor/companies?shortname=${props.shortname}`}
                as={`/investors/${props.shortname}#companies`}
              >
                <a className="company-logo-display-all">
                  <HoverEffect>
                    <CompanyLogo.Frame size={'large'}>
                      <span
                        css={css`
                          color: ${colors.link};
                          text-decoration: underline;
                          font-size: 13px;
                        `}
                      >
                        すべて見る
                      </span>
                    </CompanyLogo.Frame>
                  </HoverEffect>
                </a>
              </Link>
            )}
          </div>
        </>
      )}
    </div>
  )
}

type HoverEffectProps = {
  children: React.ReactNode
}

const HoverEffect: React.FC<HoverEffectProps> = (props) => {
  return (
    <div
      css={css`
        transition: 0.3s;
        &:hover {
          box-shadow: rgba(22, 22, 22, 0.1) 0px 0px 15px 0px;
        }
      `}
    >
      {props.children}
    </div>
  )
}
